import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DashboardLayout } from '../../components/layout'
import { Button, Tabs, StatCard, Modal, Badge } from '../../components/ui'
import { BusinessSettings } from '../../features/businesses'
import { TransactionTable, TransactionForm } from '../../features/transactions'
import { SubscriptionList, SubscriptionForm } from '../../features/subscriptions'
import { TodoList, TodoForm } from '../../features/todos'
import { useBusiness, useBusinesses } from '../../hooks/useBusinesses'
import { useTransactions } from '../../hooks/useTransactions'
import { useSubscriptions } from '../../hooks/useSubscriptions'
import { useTodos } from '../../hooks/useTodos'
import { useToast } from '../../context/ToastContext'
import { formatCurrency } from '../../lib/format'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { db } from '../../lib/supabase'

function BusinessPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const { business, loading, updateBusiness } = useBusiness(id)
  const { deleteBusiness } = useBusinesses()
  const {
    transactions,
    financials,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    refetch: refetchTransactions,
  } = useTransactions(id)
  const {
    subscriptions,
    monthlyTotal: subscriptionMonthly,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    refetch: refetchSubscriptions,
  } = useSubscriptions(id)
  const {
    todos,
    counts: todoCounts,
    createTodo,
    updateTodo,
    deleteTodo,
    toggleComplete,
    refetch: refetchTodos,
  } = useTodos(id)

  const [activeTab, setActiveTab] = useState('transactions')
  const [showTransactionModal, setShowTransactionModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showTodoModal, setShowTodoModal] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [formLoading, setFormLoading] = useState(false)

  if (loading) {
    return (
      <DashboardLayout title="Loading...">
        <LoadingSpinner.Overlay />
      </DashboardLayout>
    )
  }

  if (!business) {
    return (
      <DashboardLayout title="Business Not Found">
        <div className="text-center py-8">
          <p className="text-muted mb-4">This business could not be found.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </DashboardLayout>
    )
  }

  const handleDeleteBusiness = async () => {
    const { error } = await deleteBusiness(id)
    if (error) {
      toast.error('Failed to delete business')
      return { error }
    }
    toast.success('Business deleted')
    navigate('/dashboard')
    return { error: null }
  }

  // Transaction handlers
  const handleTransactionSubmit = async (formData) => {
    setFormLoading(true)
    const { error } = editItem
      ? await updateTransaction(editItem.id, formData)
      : await createTransaction(formData)
    setFormLoading(false)

    if (error) {
      toast.error(`Failed to ${editItem ? 'update' : 'add'} transaction`)
      return
    }

    toast.success(`Transaction ${editItem ? 'updated' : 'added'}`)
    setShowTransactionModal(false)
    setEditItem(null)
    refetchTransactions()
  }

  const handleTransactionDelete = async (transactionId) => {
    const { error } = await deleteTransaction(transactionId)
    if (error) {
      toast.error('Failed to delete transaction')
      return
    }
    toast.success('Transaction deleted')
    refetchTransactions()
  }

  // Subscription handlers
  const handleSubscriptionSubmit = async (formData) => {
    setFormLoading(true)
    const { error } = editItem
      ? await updateSubscription(editItem.id, formData)
      : await createSubscription({ ...formData, business_id: id })
    setFormLoading(false)

    if (error) {
      toast.error(`Failed to ${editItem ? 'update' : 'add'} subscription`)
      return
    }

    toast.success(`Subscription ${editItem ? 'updated' : 'added'}`)
    setShowSubscriptionModal(false)
    setEditItem(null)
    refetchSubscriptions()
  }

  const handleSubscriptionDelete = async (subscriptionId) => {
    const { error } = await deleteSubscription(subscriptionId)
    if (error) {
      toast.error('Failed to delete subscription')
      return
    }
    toast.success('Subscription deleted')
    refetchSubscriptions()
  }

  // Todo handlers
  const handleTodoSubmit = async (formData) => {
    setFormLoading(true)
    const { error } = editItem
      ? await updateTodo(editItem.id, formData)
      : await createTodo({ ...formData, business_id: id })
    setFormLoading(false)

    if (error) {
      toast.error(`Failed to ${editItem ? 'update' : 'add'} task`)
      return
    }

    toast.success(`Task ${editItem ? 'updated' : 'added'}`)
    setShowTodoModal(false)
    setEditItem(null)
    refetchTodos()
  }

  const handleTodoDelete = async (todoId) => {
    const { error } = await deleteTodo(todoId)
    if (error) {
      toast.error('Failed to delete task')
      return
    }
    toast.success('Task deleted')
    refetchTodos()
  }

  const handleTodoToggle = async (todoId) => {
    const { error } = await toggleComplete(todoId)
    if (error) {
      toast.error('Failed to update task')
    }
  }

  const statusBadge = {
    active: <Badge variant="active">Active</Badge>,
    planning: <Badge variant="planning">Planning</Badge>,
    paused: <Badge variant="paused">Paused</Badge>,
  }

  return (
    <DashboardLayout
      title={
        <div className="flex items-center gap-3">
          <span className="text-2xl">{business.emoji || '🏢'}</span>
          <span>{business.name}</span>
          {statusBadge[business.status]}
        </div>
      }
    >
      {/* Stats */}
      <div className="grid grid-4 mb-6">
        <StatCard
          label="Revenue"
          value={formatCurrency(financials.revenue)}
          changeType="positive"
        />
        <StatCard
          label="Expenses"
          value={formatCurrency(financials.expenses)}
          changeType="negative"
        />
        <StatCard
          label="Profit"
          value={formatCurrency(financials.profit)}
          changeType={financials.profit >= 0 ? 'positive' : 'negative'}
        />
        <StatCard
          label="Monthly Subscriptions"
          value={formatCurrency(subscriptionMonthly)}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <div className="flex justify-between items-center mb-4">
          <Tabs.List>
            <Tabs.Tab value="transactions">
              Transactions ({transactions.length})
            </Tabs.Tab>
            <Tabs.Tab value="subscriptions">
              Subscriptions ({subscriptions.length})
            </Tabs.Tab>
            <Tabs.Tab value="todos">
              Todos ({todoCounts.pending})
            </Tabs.Tab>
            <Tabs.Tab value="settings">Settings</Tabs.Tab>
          </Tabs.List>

          {activeTab === 'transactions' && (
            <Button onClick={() => { setEditItem(null); setShowTransactionModal(true) }}>
              + Add Transaction
            </Button>
          )}
          {activeTab === 'subscriptions' && (
            <Button onClick={() => { setEditItem(null); setShowSubscriptionModal(true) }}>
              + Add Subscription
            </Button>
          )}
          {activeTab === 'todos' && (
            <Button onClick={() => { setEditItem(null); setShowTodoModal(true) }}>
              + Add Task
            </Button>
          )}
        </div>

        <Tabs.Content value="transactions">
          <TransactionTable
            transactions={transactions}
            onEdit={(t) => { setEditItem(t); setShowTransactionModal(true) }}
            onDelete={handleTransactionDelete}
            onAdd={() => { setEditItem(null); setShowTransactionModal(true) }}
          />
        </Tabs.Content>

        <Tabs.Content value="subscriptions">
          <SubscriptionList
            subscriptions={subscriptions}
            onEdit={(s) => { setEditItem(s); setShowSubscriptionModal(true) }}
            onDelete={handleSubscriptionDelete}
            onAdd={() => { setEditItem(null); setShowSubscriptionModal(true) }}
          />
        </Tabs.Content>

        <Tabs.Content value="todos">
          <TodoList
            todos={todos}
            onEdit={(t) => { setEditItem(t); setShowTodoModal(true) }}
            onDelete={handleTodoDelete}
            onToggle={handleTodoToggle}
            onAdd={() => { setEditItem(null); setShowTodoModal(true) }}
          />
        </Tabs.Content>

        <Tabs.Content value="settings">
          <BusinessSettings
            business={business}
            onUpdate={updateBusiness}
            onDelete={handleDeleteBusiness}
          />
        </Tabs.Content>
      </Tabs>

      {/* Transaction Modal */}
      <Modal
        isOpen={showTransactionModal}
        onClose={() => { setShowTransactionModal(false); setEditItem(null) }}
        title={editItem ? 'Edit Transaction' : 'Add Transaction'}
      >
        <TransactionForm
          initialData={editItem}
          onSubmit={handleTransactionSubmit}
          onCancel={() => { setShowTransactionModal(false); setEditItem(null) }}
          loading={formLoading}
        />
      </Modal>

      {/* Subscription Modal */}
      <Modal
        isOpen={showSubscriptionModal}
        onClose={() => { setShowSubscriptionModal(false); setEditItem(null) }}
        title={editItem ? 'Edit Subscription' : 'Add Subscription'}
      >
        <SubscriptionForm
          initialData={editItem}
          businesses={[business]}
          onSubmit={handleSubscriptionSubmit}
          onCancel={() => { setShowSubscriptionModal(false); setEditItem(null) }}
          loading={formLoading}
        />
      </Modal>

      {/* Todo Modal */}
      <Modal
        isOpen={showTodoModal}
        onClose={() => { setShowTodoModal(false); setEditItem(null) }}
        title={editItem ? 'Edit Task' : 'Add Task'}
      >
        <TodoForm
          initialData={editItem}
          businesses={[business]}
          onSubmit={handleTodoSubmit}
          onCancel={() => { setShowTodoModal(false); setEditItem(null) }}
          loading={formLoading}
        />
      </Modal>
    </DashboardLayout>
  )
}

export default BusinessPage
